import { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, useToast } from '@bonusly/design-system';

import {
	parseBizyProfilePatchError,
	useBizyUpdateProfileMutation,
	type BizyProfilePatchFieldError,
	type BizyProfilePatchPayload,
} from '../../queries/bizy-profile';
import { setBizyPanelMode } from '../bizy-side-panel/lib/panel-preferences';
import { useBizySidePanelStore } from '../bizy-side-panel/store/use-bizy-side-panel-store';
import { useBizySettingsDraftStore, useBizySettingsHasDrafts } from './store/use-bizy-settings-draft-store';

const fieldLabelFor = (t: TFunction<'admin_bizy'>, field: string): string => t(`settingsPanel.profile.fields.${field}.label`, { defaultValue: field });

const errorTitleFor = (t: TFunction<'admin_bizy'>, fieldError: BizyProfilePatchFieldError): string => {
	const label = fieldLabelFor(t, fieldError.field);
	switch (fieldError.reason) {
		case 'too_long':
			return t('settingsPanel.saveBar.errors.tooLong', { field: label, maxLength: fieldError.maxLength });
		case 'rejected':
			return t('settingsPanel.saveBar.errors.rejected', { field: label });
		case 'invalid_field':
			return t('settingsPanel.saveBar.errors.invalidField', { field: label });
		default:
			return t('settingsPanel.saveBar.errors.generic');
	}
};

// ─── Bizy Settings Save Bar ─────────────────────────────────────────────────
//
// Sticky bottom bar shown whenever the General tab has unsaved drafts. Save
// commits all pending changes (profile mutation + local preferences + side
// panel state); Cancel discards them.

export const BizySettingsSaveBar = () => {
	const { t } = useTranslation('admin_bizy');
	const hasDrafts = useBizySettingsHasDrafts();
	const clearAllDrafts = useBizySettingsDraftStore((state) => state.clearAllDrafts);
	const updateMutation = useBizyUpdateProfileMutation();
	const isPanelOpen = useBizySidePanelStore((state) => state.isOpen);
	const toast = useToast();
	const [isSaving, setIsSaving] = useState(false);

	if (!hasDrafts) return null;

	const handleCancel = () => {
		clearAllDrafts();
	};

	const handleSave = async () => {
		setIsSaving(true);

		const drafts = useBizySettingsDraftStore.getState();
		const { pendingProfileFields, pendingDefaultChatSize } = drafts;

		if (Object.keys(pendingProfileFields).length > 0) {
			const payload: BizyProfilePatchPayload = Object.fromEntries(
				Object.entries(pendingProfileFields).map(([field, draftValue]) => [field, draftValue.trim() === '' ? null : draftValue.trim()])
			);
			try {
				await updateMutation.mutateAsync(payload);
			} catch (error) {
				const fieldError = parseBizyProfilePatchError(error);
				toast({
					title: fieldError ? errorTitleFor(t, fieldError) : t('settingsPanel.saveBar.errors.generic'),
					status: 'error',
					duration: 4000,
				});
				setIsSaving(false);
				return;
			}
		}

		if (pendingDefaultChatSize !== null) {
			setBizyPanelMode(pendingDefaultChatSize);
			if (isPanelOpen) {
				useBizySidePanelStore.setState({
					isFullscreen: pendingDefaultChatSize === 'fullscreen',
					isHistoryDrawerOpen: pendingDefaultChatSize === 'fullscreen',
				});
			}
		}

		clearAllDrafts();
		setIsSaving(false);
		toast({ title: t('settingsPanel.saveBar.saved'), status: 'success', duration: 2500 });
	};

	return (
		<Flex align="center" justify="flex-end" gap={2} px={5} py={3} bg="bg.surface" borderTop="1px solid" borderColor="border.soft" flexShrink={0}>
			<Button variant="outline" colorScheme="secondary" size="md" onClick={handleCancel} isDisabled={isSaving}>
				{t('settingsPanel.cancel')}
			</Button>
			<Button colorScheme="primary" size="md" onClick={handleSave} isLoading={isSaving}>
				{t('settingsPanel.save')}
			</Button>
		</Flex>
	);
};
